export interface AdminUser {
  email: string;
  role: string;
}

class AdminService {
  private readonly userStorageKey = 'csd_admin_user';

  public async authenticate(
    email: string,
    password: string
  ): Promise<{ user: AdminUser }> {
    const response = await fetch('/api/admin/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ email, password }),
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok || !payload?.user) {
      throw new Error(payload?.error || 'Email ou mot de passe incorrect');
    }

    this.storeUser(payload.user);
    return {
      user: payload.user,
    };
  }

  public async validateSession(): Promise<boolean> {
    try {
      const response = await fetch('/api/admin/session', {
        credentials: 'same-origin',
        cache: 'no-store',
      });

      if (!response.ok) {
        return false;
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        return false;
      }

      const payload = await response.json().catch(() => null);
      return payload?.authenticated === true;
    } catch {
      return false;
    }
  }

  private storeUser(user: AdminUser) {
    localStorage.setItem(this.userStorageKey, JSON.stringify(user));
  }

  public getStoredUser(): AdminUser | null {
    try {
      const rawValue = localStorage.getItem(this.userStorageKey);
      return rawValue ? (JSON.parse(rawValue) as AdminUser) : null;
    } catch {
      return null;
    }
  }

  public clearStoredUser() {
    localStorage.removeItem(this.userStorageKey);
  }

  public async logout() {
    await fetch('/api/admin/logout', {
      method: 'POST',
      credentials: 'same-origin',
    }).catch(() => undefined);
    this.clearStoredUser();
  }
}

export const adminService = new AdminService();
